/**
 * First-party visitor identity for public pages.
 *
 * `lh_anon_id` is written to a cookie and localStorage on the first visit.
 * The session id rotates after 30 minutes without a tracked event.
 * First touch is immutable. Last touch updates when a visit carries a
 * campaign signal (utm, fbclid, gclid, or the Lemlist `r` token).
 */
import { isThemeExemptPath } from '@/theme/appearance'
import {
    applyTouch,
    deviceFromUserAgent,
    nextSessionId,
    normalizePath,
    shouldStitchIdentity,
    touchFromSearch,
    type Touch,
} from '@/analytics/attribution'

export type { Touch }
export {
    applyTouch,
    deviceFromUserAgent,
    nextSessionId,
    normalizePath,
    shouldStitchIdentity,
    touchFromSearch,
}

export const ANON_COOKIE = 'lh_anon_id'
export const ANON_STORAGE_KEY = 'lh_anon_id'
export const SESSION_ID_KEY = 'lh_session_id'
export const SESSION_SEEN_KEY = 'lh_session_seen_at'
export const FIRST_TOUCH_KEY = 'lh_first_touch'
export const LAST_TOUCH_KEY = 'lh_last_touch'
export const STITCH_PENDING_KEY = 'lh_stitch_pending'
export const STITCHED_KEY = 'lh_stitched_anon'
export const ANON_COOKIE_DAYS = 730

const EXTRA_PUBLIC = new Set([
    '/verify',
    '/reset-password',
    '/native-login',
    '/oauth-callback',
    '/auth/callback',
    '/tour',
    '/unsubscribe',
    '/404',
])

export function isPublicTrackingPath(path: string): boolean {
    const pathname = normalizePath(path)
    if (EXTRA_PUBLIC.has(pathname)) return true
    if (pathname === '/tour' || pathname.startsWith('/tour/')) return true
    return isThemeExemptPath(pathname)
}

export function isLandingPath(path: string): boolean {
    const pathname = normalizePath(path)
    return pathname === '/' || pathname === '/launch' || pathname.startsWith('/lp/')
}

function readCookie(name: string): string | null {
    if (typeof document === 'undefined') return null
    try {
        const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
        return match ? decodeURIComponent(match[1]) : null
    }
    catch {
        return null
    }
}

function writeCookie(name: string, value: string, days: number) {
    if (typeof document === 'undefined') return
    try {
        const expires = new Date(Date.now() + days * 86400 * 1000).toUTCString()
        const secure = location.protocol === 'https:' ? '; Secure' : ''
        document.cookie = `${name}=${encodeURIComponent(value)}; path=/; expires=${expires}; SameSite=Lax${secure}`
    }
    catch {
        // localStorage still holds the id
    }
}

function readStorage(key: string): string | null {
    try {
        return localStorage.getItem(key)
    }
    catch {
        return null
    }
}

function writeStorage(key: string, value: string) {
    try {
        localStorage.setItem(key, value)
    }
    catch {
        // private mode
    }
}

function newId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID()
    }
    return 'xxxxxxxx-xxxx-4xxx-8xxx-xxxxxxxxxxxx'.replace(/[x]/g, () =>
        Math.floor(Math.random() * 16).toString(16))
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function validId(value: string | null): string | null {
    if (!value || !UUID_RE.test(value)) return null
    return value.toLowerCase()
}

export function readAnonId(): string | null {
    return validId(readCookie(ANON_COOKIE)) || validId(readStorage(ANON_STORAGE_KEY))
}

export function ensureAnonId(): string {
    const fromCookie = validId(readCookie(ANON_COOKIE))
    const fromStore = validId(readStorage(ANON_STORAGE_KEY))
    const id = fromCookie || fromStore || newId()
    if (fromCookie !== id) writeCookie(ANON_COOKIE, id, ANON_COOKIE_DAYS)
    if (fromStore !== id) writeStorage(ANON_STORAGE_KEY, id)
    return id
}

export function currentSessionId(now = Date.now()): string {
    const previous = validId(readStorage(SESSION_ID_KEY))
    const seenRaw = readStorage(SESSION_SEEN_KEY)
    const seen = seenRaw ? Number(seenRaw) : null
    const next = nextSessionId(previous, seen, now, newId)
    if (next.id !== previous) writeStorage(SESSION_ID_KEY, next.id)
    writeStorage(SESSION_SEEN_KEY, String(now))
    return next.id
}

export function readTouches(): { first: Touch | null, last: Touch | null } {
    return {
        first: parseTouch(readStorage(FIRST_TOUCH_KEY)) || parseTouch(readCookie(FIRST_TOUCH_KEY)),
        last: parseTouch(readStorage(LAST_TOUCH_KEY)) || parseTouch(readCookie(LAST_TOUCH_KEY)),
    }
}

function parseTouch(raw: string | null): Touch | null {
    if (!raw) return null
    try {
        const parsed = JSON.parse(raw) as Touch
        if (!parsed || typeof parsed !== 'object' || !parsed.captured_at) return null
        return parsed
    }
    catch {
        return null
    }
}

export function captureAttribution(now = new Date()): { first: Touch, last: Touch } {
    const device = deviceFromUserAgent(typeof navigator === 'undefined' ? '' : navigator.userAgent)
    const incoming = touchFromSearch({
        search: typeof window === 'undefined' ? '' : window.location.search,
        referrer: typeof document === 'undefined' ? '' : document.referrer,
        path: typeof window === 'undefined' ? '/' : window.location.pathname,
        device,
        now: now.toISOString(),
        sameOrigin: typeof window === 'undefined' ? undefined : window.location.origin,
    })
    const stored = readTouches()
    const next = applyTouch(stored.first, stored.last, incoming)
    const firstJson = JSON.stringify(next.first)
    const lastJson = JSON.stringify(next.last)
    writeStorage(FIRST_TOUCH_KEY, firstJson)
    writeStorage(LAST_TOUCH_KEY, lastJson)
    // Logout clears localStorage. The cookie keeps first and last touch.
    writeCookie(FIRST_TOUCH_KEY, firstJson, ANON_COOKIE_DAYS)
    writeCookie(LAST_TOUCH_KEY, lastJson, ANON_COOKIE_DAYS)
    return next
}

export function ensureIdentity(): { anonId: string, sessionId: string, first: Touch, last: Touch } {
    const anonId = ensureAnonId()
    const sessionId = currentSessionId()
    const touches = captureAttribution()
    return { anonId, sessionId, ...touches }
}

export function markStitchPending(now = new Date().toISOString()) {
    writeStorage(STITCH_PENDING_KEY, now)
}

export function readStitchPending(): string | null {
    return readStorage(STITCH_PENDING_KEY)
}

export function clearStitchPending() {
    try {
        localStorage.removeItem(STITCH_PENDING_KEY)
    }
    catch {
        // ignore
    }
}

export function markStitched(anonId: string) {
    writeStorage(STITCHED_KEY, anonId)
    clearStitchPending()
}

export function alreadyStitched(anonId: string): boolean {
    return readStorage(STITCHED_KEY) === anonId
}

export function currentDevice(): string {
    return deviceFromUserAgent(typeof navigator === 'undefined' ? '' : navigator.userAgent)
}
